import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SalesforceOpportunityStage,
  salesforceOpportunityStagesQueryKeys,
} from '@/hooks/salesforce/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'

interface UseSalesforceOpportunityStagesArgs<T> {
  orgId: UUID | ''
  select?: (data: PaginatedResponse<SalesforceOpportunityStage>) => T
}

export const fetchSalesforceOpportunityStages = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<SalesforceOpportunityStage>> => {
  if (!orgId) throw new Error('No organization ID provided.')
  const url = `${API_URL}/api/v1/organizations/${orgId}/salesforce/opportunity-stages`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as PaginatedResponse<SalesforceOpportunityStage>
}

export const useSalesforceOpportunityStages = <
  T = SalesforceOpportunityStage[],
>({
  orgId,
  select,
}: UseSalesforceOpportunityStagesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<SalesforceOpportunityStage>) => {
      if (select) {
        return select(data) as unknown as T
      }
      return data.data as unknown as T
    },
    [select],
  )
  return useQuery({
    queryKey: salesforceOpportunityStagesQueryKeys.organization({ orgId }),
    queryFn: () => fetchSalesforceOpportunityStages(orgId, accessToken),
    enabled: !!orgId,
    staleTime: Infinity,
    select: selectFn,
  })
}
